import { Injectable, computed, signal } from '@angular/core';
import { ApiFailureKey } from '../http/api-failure-policy';

export interface CapturedApiFailure {
  key: ApiFailureKey;
  purpose: string;
  method: string;
  url: string;
  message: string;
  status: number;
  capturedAt: number;
}

/**
 * App-wide API failure banner + Try Again capture.
 * Session expiry is handled only in the error interceptor → login via AuthStateService.
 *
 * Features register a refresh handler per failure key; Try Again invokes only those
 * handlers for currently captured retryable failures (not a full page reload).
 */
@Injectable({ providedIn: 'root' })
export class ApiErrorService {
  private readonly messageSignal = signal<string | null>(null);
  private readonly failuresSignal = signal<CapturedApiFailure[]>([]);
  private readonly refreshHandlers = new Map<ApiFailureKey, () => void>();
  /** 402 Payment Required — show Billing CTA beside the banner message. */
  private readonly paymentRequiredSignal = signal(false);

  readonly message = this.messageSignal.asReadonly();
  readonly failures = this.failuresSignal.asReadonly();
  readonly hasRetryableFailures = computed(() => this.failuresSignal().length > 0);
  readonly paymentRequired = this.paymentRequiredSignal.asReadonly();

  set(message: string): void {
    const text = message.trim();
    this.messageSignal.set(text || null);
    this.paymentRequiredSignal.set(false);
  }

  /** Banner for subscription gate (HTTP 402) — keep backend detail, offer Billing. */
  setPaymentRequired(message: string): void {
    const text = message.trim();
    this.messageSignal.set(text || null);
    this.paymentRequiredSignal.set(!!text);
  }

  /**
   * Record a classified failure for Try Again. Dedupes by key (latest wins).
   * Always updates the banner message when provided.
   */
  capture(failure: CapturedApiFailure): void {
    const text = failure.message.trim();
    if (text) {
      this.messageSignal.set(text);
    }
    this.paymentRequiredSignal.set(false);
    this.failuresSignal.update((list) => {
      const without = list.filter((f) => f.key !== failure.key);
      return [...without, failure];
    });
  }

  /** Drop banner + capture without re-calling APIs. */
  clear(): void {
    this.messageSignal.set(null);
    this.failuresSignal.set([]);
    this.paymentRequiredSignal.set(false);
  }

  /** Same as clear — explicit name for the banner X control. */
  dismiss(): void {
    this.clear();
  }

  /**
   * Register how to reload data for a failure key.
   * Returns an unregister function (call from destroy).
   */
  registerRefreshHandler(key: ApiFailureKey, handler: () => void): () => void {
    this.refreshHandlers.set(key, handler);
    return () => {
      if (this.refreshHandlers.get(key) === handler) {
        this.refreshHandlers.delete(key);
      }
    };
  }

  /**
   * Re-invoke only refresh handlers for currently captured failures.
   * Capture is cleared when those requests succeed (see noteSuccess).
   */
  retryCaptured(): void {
    const keys = [...new Set(this.failuresSignal().map((f) => f.key))];
    if (keys.length === 0) {
      return;
    }
    for (const key of keys) {
      this.refreshHandlers.get(key)?.();
    }
  }

  /** Remove a captured failure after its reload succeeds; clear banner when none left. */
  noteSuccess(url: string, method: string): void {
    const verb = method.toUpperCase();
    if (verb !== 'GET') {
      return;
    }
    const path = url.toLowerCase();
    this.failuresSignal.update((list) => {
      const next = list.filter((f) => !this.urlMatchesKey(path, f.key));
      if (next.length === 0) {
        this.messageSignal.set(null);
      }
      return next;
    });
  }

  private urlMatchesKey(path: string, key: ApiFailureKey): boolean {
    switch (key) {
      case 'accounts':
        return path.includes('/accounts');
      case 'carriers':
        return path.includes('/carriers');
      case 'senders':
        return path.includes('/senders');
      case 'jobs':
        return path.includes('/jobs');
      case 'audit':
        return path.includes('/audit');
      case 'uploads':
        return path.includes('/uploads');
      case 'admin-users':
        return path.includes('/auth/users');
      case 'admin-invites':
        return path.includes('/auth/invites');
      default:
        return false;
    }
  }
}
